// AuthCallback — processes the Emergent OAuth session_id fragment.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BookOpenText, PenLine } from "lucide-react";

export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [needsRole, setNeedsRole] = useState(false);
  const [error, setError] = useState("");
  const [role, setRole] = useState("reader");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { setError("Missing session_id"); return; }
    const session_id = decodeURIComponent(m[1]);

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        localStorage.setItem("arc_token", data.token);
        // clear hash so we don't reprocess
        window.history.replaceState({}, "", "/auth/callback");
        if (data.needs_role) {
          setUser(data.user);
          setDisplayName(data.user.display_name || "");
          setNeedsRole(true);
        } else {
          setUser(data.user);
          toast.success(`Welcome, ${data.user.display_name}`);
          nav(data.user.role === "author" ? "/author" : "/reader", { replace: true });
        }
      } catch (e) {
        setError(apiError(e));
      }
    })();
  }, [location, nav, setUser]);

  const completeSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/google/complete-signup", { role, display_name: displayName });
      localStorage.setItem("arc_token", data.token);
      setUser(data.user);
      toast.success(`Welcome to Faraway Sister Haven, ${data.user.display_name}!`);
      nav(data.user.role === "author" ? "/author" : "/reader", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="paper-card p-8 text-center">
          <h1 className="rainbow-text text-2xl mb-3">Sign-in failed</h1>
          <p className="text-sm mb-4" style={{ color: "var(--ink-soft)" }}>{error}</p>
          <Button className="btn-press btn-rainbow" onClick={() => nav("/login")} data-testid="callback-back-btn">Back to login</Button>
        </div>
      </div>
    );
  }

  if (needsRole) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="paper-card p-8">
          <div className="overline mb-2">One last step</div>
          <h1 className="rainbow-text text-3xl mb-6">Finish your profile</h1>

          <form onSubmit={completeSignup} className="space-y-4" data-testid="callback-role-form">
            <div>
              <Label>Display name</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required minLength={1} data-testid="callback-name-input" />
            </div>
            <div>
              <Label>I am a…</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setRole("reader")}
                  className={`btn-press rounded-xl border p-4 text-left ${role === "reader" ? "" : "opacity-70"}`}
                  style={{ borderColor: role === "reader" ? "#00C7FF" : "var(--border-soft)" }}
                  data-testid="callback-role-reader"
                >
                  <BookOpenText size={18} className="mb-2 rainbow-text" />
                  <div className="text-lg">Reader</div>
                  <div className="text-xs" style={{ color: "var(--ink-soft)" }}>Apply to review ARCs</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRole("author")}
                  className={`btn-press rounded-xl border p-4 text-left ${role === "author" ? "" : "opacity-70"}`}
                  style={{ borderColor: role === "author" ? "#E040FB" : "var(--border-soft)" }}
                  data-testid="callback-role-author"
                >
                  <PenLine size={18} className="mb-2 rainbow-text" />
                  <div className="text-lg">Author</div>
                  <div className="text-xs" style={{ color: "var(--ink-soft)" }}>Post &amp; manage campaigns</div>
                </button>
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full btn-press btn-rainbow" data-testid="callback-submit-btn">
              {submitting ? "Setting up…" : "Enter Faraway Sister Haven"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <div className="paper-card p-8">
        <div className="rainbow-text text-2xl">Signing you in…</div>
        <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>Trading stardust for a session.</p>
      </div>
    </div>
  );
}
